/**
 * Multi-value fields rendered as individually clickable chips.
 *
 * Tags routinely pack several values into one string -- "Afrobeats; Alté",
 * "Amapiano / Private School", or a comment used as an ad-hoc field store like
 * "mood: chill, energy: high". Showing that as one opaque string makes the
 * information unusable; showing each part as a chip that filters the library
 * turns tags a station has been keeping for years into a working index.
 *
 * **Splitting is a heuristic and it is wrong sometimes.** "Drum & Bass" is one
 * genre; "Earth, Wind & Fire" is one artist. The rule below refuses to split
 * where a separator is not surrounded the way a list separator normally is, but
 * it cannot be right in every case. That is why the original string is always
 * available on hover: a curator can see what the tag actually says.
 */

/** Split a packed tag value into its parts. */
export function splitValues(raw: string | null): string[] {
  if (!raw) return [];

  // The ID3 null separator is unambiguous -- if present, trust it alone.
  if (raw.includes("\u0000")) {
    return raw.split("\u0000").map((s) => s.trim()).filter(Boolean);
  }

  // Semicolon and slash are near-unambiguous as list separators in tags.
  // Comma is not: it appears inside single values far too often, so it only
  // splits when nothing stronger is present AND every part looks like a label
  // rather than prose.
  const strong = /\s*[;/|]\s*/;
  if (strong.test(raw)) {
    return raw.split(strong).map((s) => s.trim()).filter(Boolean);
  }

  if (raw.includes(",")) {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    const looksLikeList =
      parts.length > 1 &&
      // A list item is short and has no sentence punctuation. Prose does not
      // survive this test, which is the point.
      parts.every((p) => p.length <= 28 && !/[.!?]/.test(p));
    if (looksLikeList) return parts;
  }

  return [raw.trim()].filter(Boolean);
}

export function Chips({
  value,
  parts: given,
  className = "",
  onPick,
  emptyLabel = "—",
  max = 4,
}: {
  value: string | null;
  /**
   * Parts as split by the engine. When absent — the offline seed path — the
   * local heuristic below stands in, which is the only reason it still exists.
   */
  parts?: string[];
  className?: string;
  /** Called with the clicked part, to add it to the query. */
  onPick: (part: string) => void;
  emptyLabel?: string;
  max?: number;
}) {
  const parts = given ?? splitValues(value);
  if (parts.length === 0) return <span className="dim">{emptyLabel}</span>;

  const shown = parts.slice(0, max);
  const overflow = parts.length - shown.length;

  return (
    <span className="chips" title={value ?? undefined}>
      {shown.map((part, i) => (
        <button
          key={`${part}-${i}`}
          type="button"
          className={`chip ${className}`}
          title={`Filter by "${part}"`}
          onClick={(e) => {
            e.stopPropagation();
            onPick(part);
          }}
        >
          {part}
        </button>
      ))}
      {overflow > 0 && (
        <span className="chip-more" title={parts.slice(max).join(", ")}>
          +{overflow}
        </span>
      )}
    </span>
  );
}
